import React, { ReactNode } from "react";
import { Accordion, AccordionSummary, Grid, Button } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { FieldArray, useFormikContext, getIn, Field } from "formik";
import { SectionWrapper } from "../../../../../UI/SectionWrapper/SectionWrapper";
import { AssetAccordionDetails } from "@client/components/CustomerPortal/ui-components/pageLayout/TableSettingsComponents";
import { FormField } from "@client/components/Form/FormField";
import {
  CMSMediaType,
  MediaResourceProtocol,
  MediaResourceType,
  VideoResolution,
} from "@client/models/CMS/cmsMedia";

/** Allowed language codes (ISO 639-1) for subtitles and audio */
export const ALLOWED_LANGUAGE_CODES = [
  { value: "sv", label: "Svenska" },
  { value: "en", label: "English" },
  { value: "no", label: "Norsk" },
  { value: "da", label: "Dansk" },
  { value: "fi", label: "Suomi" },
] as const;

type MediasAccordionProps = {
  /** base path in the parent values, e.g. "medias" or "asset.medias" */
  name?: string;
  /** optional content rendered above the medias list (e.g. VOD upload, subtitle upload) */
  topSection?: ReactNode;
  /** if true, all fields are readonly and add/remove buttons are hidden (e.g. for catchup assets) */
  readonly?: boolean;
};

export const newMedia = () => ({
  type: CMSMediaType.Main,
  name: "",
  videoResolution: VideoResolution.HD,
  durationMillis: 0,
  videoWidth: 0,
  videoHeight: 0,
  multiChannelAudio: false,
  spokenLanguage: "",
  audioLanguages: [{ language: "" }],
  subtitleLanguages: [{ language: "" }],
  hardOfHearing: false,
  enabled: false,
  mediaResources: [
    {
      type: MediaResourceType.Native,
      protocol: MediaResourceProtocol.DASH,
      url: "",
      isEncrypted: false,
      //createdAt: new Date().toISOString(),
      //updatedAt: new Date().toISOString(),
    },
  ],
});

const newMediaResource = () => ({
  type: MediaResourceType.Native,
  protocol: MediaResourceProtocol.DASH,
  url: "",
  isEncrypted: false,
});

const MediasAccordion: React.FC<MediasAccordionProps> = ({
  name = "medias",
  topSection,
  readonly = false,
}) => {
  const { values } = useFormikContext<any>();
  const medias = getIn(values, name) as any[] | undefined;

  return (
    <SectionWrapper>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          className="accordion-summary-gray"
        >
          Medias
        </AccordionSummary>

        <AssetAccordionDetails>
          {readonly && (
            <div
              style={{
                padding: "12px 16px",
                marginBottom: 16,
                backgroundColor: "#f3f4f6",
                borderLeft: "4px solid #3b82f6",
                borderRadius: 4,
              }}
            >
              <strong>Read-only:</strong> Catchup assets use their source
              channel&apos;s media resources. Edit the source channel to modify.
            </div>
          )}
          {topSection}
          <FieldArray name={name}>
            {({ push, remove }) => (
              <>
                {(medias ?? []).length === 0 ? (
                  !readonly && (
                    <Button
                      variant="contained"
                      onClick={() => push(newMedia())}
                    >
                      + Add media
                    </Button>
                  )
                ) : (
                  <>
                    {(medias ?? []).map((m, i) => {
                      const base = `${name}[${i}]`;
                      const fromMpd = Boolean(
                        getIn(values, `${base}._fromMpd`) ||
                          getIn(values, `${base}._durationFromAlcom`)
                      );
                      const isReadOnly = readonly || fromMpd;
                      return (
                        <div
                          key={i}
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            padding: 16,
                            marginBottom: 16,
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={3}>
                              <FormField
                                name={`${base}.videoWidth`}
                                label="Width"
                                placeholder="e.g. 1920"
                                readOnly={isReadOnly}
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <FormField
                                name={`${base}.videoHeight`}
                                label="Height"
                                placeholder="e.g. 1080"
                                readOnly={isReadOnly}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <FormField
                                name={`${base}.durationMillis`}
                                label="Duration (ms)"
                                placeholder="e.g. 60000"
                                readOnly={isReadOnly}
                              />
                            </Grid>
                            {/* Audio Languages */}
                            <Grid item xs={12}>
                              <FieldArray name={`${base}.audioLanguages`}>
                                {({ push, remove }) => (
                                  <div style={{ marginTop: 8 }}>
                                    <b>Audio Languages</b>
                                    {(m.audioLanguages ?? []).map(
                                      (_: any, j: number) => (
                                        <Grid
                                          container
                                          spacing={1}
                                          key={j}
                                          alignItems="center"
                                          style={{ marginTop: 8 }}
                                        >
                                          <Grid item xs={6}>
                                            <FormField
                                              label="Audio Language"
                                              name={`${base}.audioLanguages[${j}].language`}
                                              select
                                              options={[
                                                ...ALLOWED_LANGUAGE_CODES,
                                              ]}
                                              readOnly={isReadOnly}
                                            />
                                          </Grid>
                                          {!isReadOnly && (
                                            <Grid item xs="auto">
                                              <Button
                                                variant="outlined"
                                                onClick={() => remove(j)}
                                              >
                                                Remove
                                              </Button>
                                            </Grid>
                                          )}
                                        </Grid>
                                      )
                                    )}
                                    {!isReadOnly && (
                                      <Button
                                        sx={{ mt: 1 }}
                                        onClick={() => push({ language: "" })}
                                      >
                                        + Add audio language
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </FieldArray>
                            </Grid>

                            {/* Subtitle Languages */}
                            <Grid item xs={12}>
                              <FieldArray name={`${base}.subtitleLanguages`}>
                                {({ push, remove }) => (
                                  <div style={{ marginTop: 8 }}>
                                    <b>Subtitle Languages</b>
                                    {(m.subtitleLanguages ?? []).map(
                                      (_: any, j: number) => (
                                        <Grid
                                          container
                                          spacing={1}
                                          key={j}
                                          alignItems="center"
                                          style={{ marginTop: 8 }}
                                        >
                                          <Grid item xs={6}>
                                            <FormField
                                              label="Language"
                                              name={`${base}.subtitleLanguages[${j}].language`}
                                              select
                                              options={[
                                                ...ALLOWED_LANGUAGE_CODES,
                                              ]}
                                              readOnly={isReadOnly}
                                            />
                                          </Grid>
                                          {!isReadOnly && (
                                            <Grid item xs="auto">
                                              <Button
                                                variant="outlined"
                                                onClick={() => remove(j)}
                                              >
                                                Remove
                                              </Button>
                                            </Grid>
                                          )}
                                        </Grid>
                                      )
                                    )}
                                    {!isReadOnly && (
                                      <Button
                                        sx={{ mt: 1 }}
                                        onClick={() => push({ language: "" })}
                                      >
                                        + Add subtitle
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </FieldArray>
                            </Grid>

                            {/* Media Resources */}
                            <Grid item xs={12}>
                              <FieldArray name={`${base}.mediaResources`}>
                                {({ push, remove }) => (
                                  <div style={{ marginTop: 8 }}>
                                    <b>Media Resources</b>
                                    {(m.mediaResources ?? []).map(
                                      (_: any, j: number) => (
                                        <Grid
                                          container
                                          spacing={1}
                                          key={j}
                                          alignItems="center"
                                          style={{ marginTop: 8 }}
                                        >
                                          <Grid item xs={2}>
                                            <FormField
                                              name={`${base}.mediaResources[${j}].type`}
                                              label="Type"
                                              select
                                              options={Object.values(
                                                MediaResourceType
                                              ).map((t) => ({
                                                value: t as string,
                                                label: t as string,
                                              }))}
                                              readOnly={isReadOnly}
                                            />
                                          </Grid>
                                          <Grid item xs={2}>
                                            <FormField
                                              name={`${base}.mediaResources[${j}].protocol`}
                                              label="Protocol"
                                              select
                                              options={Object.values(
                                                MediaResourceProtocol
                                              ).map((t) => ({
                                                value: t,
                                                label: t,
                                              }))}
                                              readOnly={isReadOnly}
                                            />
                                          </Grid>
                                          <Grid item xs={1}>
                                            <label
                                              htmlFor={`${base}.mediaResources[${j}].isEncrypted`}
                                              style={{
                                                display: "block",
                                                fontSize: "0.75rem",
                                                marginBottom: 4,
                                              }}
                                            >
                                              Encrypted
                                            </label>
                                            <Field
                                              name={`${base}.mediaResources[${j}].isEncrypted`}
                                            >
                                              {({ field, form }: any) => (
                                                <input
                                                  type="checkbox"
                                                  id={`${base}.mediaResources[${j}].isEncrypted`}
                                                  checked={field.value === true}
                                                  disabled={isReadOnly}
                                                  onChange={(e) => {
                                                    form.setFieldValue(
                                                      field.name,
                                                      e.target.checked
                                                    );
                                                  }}
                                                />
                                              )}
                                            </Field>
                                          </Grid>
                                          <Grid item xs={6}>
                                            <FormField
                                              name={`${base}.mediaResources[${j}].url`}
                                              label="URL"
                                              placeholder="https://…"
                                              readOnly={isReadOnly}
                                            />
                                          </Grid>
                                          {!isReadOnly && (
                                            <Grid item xs="auto">
                                              <Button
                                                variant="outlined"
                                                onClick={() => remove(j)}
                                              >
                                                Remove
                                              </Button>
                                            </Grid>
                                          )}
                                        </Grid>
                                      )
                                    )}
                                    {!isReadOnly && (
                                      <Button
                                        sx={{ mt: 1 }}
                                        onClick={() => push(newMediaResource())}
                                      >
                                        + Add resource
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </FieldArray>
                            </Grid>

                            {!isReadOnly && (
                              <Grid item xs={12}>
                                <Button
                                  color="error"
                                  variant="outlined"
                                  onClick={() => remove(i)}
                                >
                                  Remove media
                                </Button>
                              </Grid>
                            )}
                          </Grid>
                        </div>
                      );
                    })}
                    {(medias ?? []).length === 0 && (
                      <Button
                        variant="contained"
                        onClick={() => push(newMedia())}
                      >
                        + Add media
                      </Button>
                    )}
                  </>
                )}
              </>
            )}
          </FieldArray>
        </AssetAccordionDetails>
      </Accordion>
    </SectionWrapper>
  );
};

export default MediasAccordion;
